import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faCheckCircle,
  faShieldAlt,
  faChartLine,
  faEnvelope,
  faUsers,
  faServer,
  faFileAlt,
  faGlobe,
  faFileInvoiceDollar,
  faEnvelopeOpenText,
  faIdCard,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "../contexts/LocaleContext";
import "../css/home.scss";
import { HeroDashboardMockup } from "./HeroDashboardMockup";
import { ConversationalCommerce } from "./ConversationalCommerce";

function RevealOnScroll({ children, className = "" }) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 transform ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      } ${className}`}
    >
      {children}
    </div>
  );
}

export function LandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isOfficialSite = window.location.hostname === "www.hsmail.vip";
  const loginUrl = isOfficialSite ? "http://dashboard.hsmail.vip" : "/login";

  const managementModules = [
    {
      id: "dashboard",
      title: t("email.dashboard.title") || "仪表盘",
      icon: faChartLine,
      description: t("email.dashboard.desc") || "数据概览与统计分析",
      features: [
        t("email.dashboard.feat1") || "发送数据统计",
        t("email.dashboard.feat2") || "趋势图表展示",
        t("email.dashboard.feat3") || "实时监控数据",
      ],
      color: "bg-blue-600",
      link: "/Home/main",
    },
    {
      id: "email-management",
      title: t("email.emails.title") || "邮件管理",
      icon: faEnvelope,
      description: t("email.emails.desc") || "邮件收发与列表管理",
      features: [
        t("email.emails.feat1") || "邮件发送功能",
        t("email.emails.feat2") || "邮件列表查看",
        t("email.emails.feat3") || "邮件搜索过滤",
      ],
      color: "bg-indigo-600",
      link: "/Home/emails",
    },
    {
      id: "user-management",
      title: t("email.users.title") || "用户管理",
      icon: faUsers,
      description: t("email.users.desc") || "系统用户与权限管理",
      features: [
        t("email.users.feat1") || "用户账号管理",
        t("email.users.feat2") || "角色权限分配",
        t("email.users.feat3") || "用户行为记录",
      ],
      color: "bg-purple-600",
      link: "/Home/users",
    },
    {
      id: "proxy-management",
      title: t("email.proxy.title") || "代理管理",
      icon: faServer,
      description: t("email.proxy.desc") || "代理服务器配置与管理",
      features: [
        t("email.proxy.feat1") || "代理服务器列表",
        t("email.proxy.feat2") || "IP隐藏与轮换",
        t("email.proxy.feat3") || "连接状态监控",
      ],
      color: "bg-teal-600",
      link: "/Home/proxy",
    },
    {
      id: "file-management",
      title: t("email.files.title") || "模板管理",
      icon: faFileAlt,
      description: t("email.files.desc") || "邮件模板创建与管理",
      features: [
        t("email.files.feat1") || "模板创建与编辑",
        t("email.files.feat2") || "模板预览与发送",
        t("email.files.feat3") || "模板状态控制",
      ],
      color: "bg-cyan-600",
      link: "/Home/files",
    },
    {
      id: "whitelist",
      title: t("email.whitelist.title") || "白名单管理",
      icon: faShieldAlt,
      description: t("email.whitelist.desc") || "可信任邮箱地址管理",
      features: [
        t("email.whitelist.feat1") || "邮箱地址白名单",
        t("email.whitelist.feat2") || "域名白名单",
        t("email.whitelist.feat3") || "快速添加管理",
      ],
      color: "bg-green-600",
      link: "/Home/whitelist",
    },
  ];

  const techFeatures = [
    {
      icon: faEnvelope,
      title: t("email.features.mail.title") || "专业邮件发送",
      description:
        t("email.features.mail.desc") ||
        "支持 SMTP/API 批量发送邮件，提供高效的邮件投递服务",
    },
    {
      icon: faShieldAlt,
      title: t("email.features.security.title") || "安全可靠",
      description:
        t("email.features.security.desc") ||
        "完善的用户权限体系和白名单机制，保障系统安全",
    },
    {
      icon: faChartLine,
      title: t("email.features.analytics.title") || "数据分析",
      description:
        t("email.features.analytics.desc") ||
        "实时统计邮件发送数据，可视化展示投递成功率和趋势",
    },
    {
      icon: faUsers,
      title: t("email.features.users.title") || "多用户管理",
      description:
        t("email.features.users.desc") ||
        "支持管理员和普通用户角色，灵活分配操作权限",
    },
    {
      icon: faFileAlt,
      title: t("email.features.files.title") || "文件管理",
      description:
        t("email.features.files.desc") ||
        "便捷的附件上传和管理，支持在线预览多种文件格式",
    },
    {
      icon: faServer,
      title: t("email.features.proxy.title") || "代理支持",
      description:
        t("email.features.proxy.desc") ||
        "灵活配置代理服务器，保护真实IP，提高发送成功率",
    },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section - Light Theme */}
      <section className="relative bg-white text-slate-900 pt-32 pb-20 md:pt-48 md:pb-32 overflow-hidden">
        {/* Abstract Background Shapes - Light Theme */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-blue-100 to-transparent rounded-bl-full transform translate-x-1/3 -translate-y-1/4 blur-3xl opacity-60"></div>
        <div className="absolute bottom-0 left-0 w-1/3 h-2/3 bg-gradient-to-tr from-cyan-100 to-transparent rounded-tr-full transform -translate-x-1/4 translate-y-1/4 blur-2xl opacity-60"></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            <div className="lg:w-1/2 text-center lg:text-left">
              <RevealOnScroll>
                <h1
                  className="text-5xl md:text-7xl font-bold tracking-tight mb-8 leading-tight"
                  style={{ lineHeight: "1.2" }}
                >
                  {t("email.hero.title1")} <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">
                    {t("email.hero.title2")}
                  </span>
                </h1>
                <p className="text-xl md:text-lg text-slate-600 mb-10 font-medium leading-relaxed">
                  {t("email.hero.description")}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                  {isOfficialSite ? (
                    <a
                      href={loginUrl}
                      className="px-8 py-4 bg-slate-900 hover:bg-blue-700 text-white rounded-full font-bold text-lg transition-all shadow-lg shadow-blue-500/20 hover:scale-105 flex items-center"
                    >
                      {t("email.hero.getStarted")}{" "}
                      <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                    </a>
                  ) : (
                    <Link
                      to="/login"
                      className="px-8 py-4 bg-slate-900 hover:bg-blue-700 text-white rounded-full font-bold text-lg transition-all shadow-lg shadow-blue-500/20 hover:scale-105 flex items-center"
                    >
                      {t("email.hero.getStarted")}{" "}
                      <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                    </Link>
                  )}
                  <Link
                    to="#products"
                    className="px-8 py-4 bg-transparent border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-full font-bold text-lg transition-all"
                    onClick={(e) => {
                      e.preventDefault();
                      document
                        .getElementById("solutions")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    {t("email.hero.exploreFeatures")}
                  </Link>
                </div>
              </RevealOnScroll>
            </div>
            <div className="w-full flex justify-center">
              <HeroDashboardMockup />
            </div>
          </div>
        </div>

        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { label: t("landing.stats.transactions"), value: "1M+" },
              { label: t("landing.stats.uptime"), value: "99.9%" },
              { label: t("landing.stats.currencies"), value: "10k+" },
              { label: t("landing.stats.customers"), value: "500+" },
            ].map((stat, idx) => (
              <RevealOnScroll key={idx} className={`delay-[${idx * 100}ms]`}>
                <div className="text-3xl md:text-4xl font-bold text-blue-900 mb-2">
                  {stat.value}
                </div>
                <div className="text-sm font-semibold uppercase tracking-wider text-slate-500">
                  {stat.label}
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
        {/* Bottom Wave Transition */}
        <div className="absolute -bottom-[1px] left-0 w-full overflow-hidden leading-[0] z-[1000]">
          <svg
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            className="relative block w-[calc(100%+1.3px)] h-[50px] transform rotate-180 "
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
              className="fill-slate-900 main-svg-fill-color"
            ></path>
          </svg>
        </div>
      </section>

      {/* Featured Solutions Section (New) */}
      <section id="solutions" className="py-24 bg-slate-900 relative z-20">
        <div className="container mx-auto px-6">
          <RevealOnScroll className="text-center mb-16">
            <span className="text-blue-600 font-bold uppercase tracking-wider text-sm">
              {t("landing.solutions.subtitle")}
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mt-3 mb-6">
              {t("landing.solutions.title")}
            </h2>
            <p className="text-xl text-white max-w-2xl mx-auto">
              {t("landing.solutions.description")}
            </p>
          </RevealOnScroll>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <RevealOnScroll className="delay-200">
              <div className="group bg-white rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-slate-200/60 hover:border-[#4d2a8a]/30 h-full flex flex-col">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-[#4d2a8a]/10 text-[#4d2a8a] rounded-xl flex items-center justify-center text-2xl group-hover:bg-[#4d2a8a] group-hover:text-white transition-colors">
                    <FontAwesomeIcon icon={faEnvelopeOpenText} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">
                    {t("landing.featured.logistics.title")}
                  </h3>
                </div>
                <p className="text-slate-600 mb-6 flex-grow leading-relaxed">
                  {t("landing.featured.logistics.desc")}
                </p>
                <ul className="space-y-2 mb-6">
                  {[
                    t("landing.featured.logistics.feat1"),
                    t("landing.featured.logistics.feat2"),
                    t("landing.featured.logistics.feat3"),
                  ].map((feat, i) => (
                    <li
                      key={i}
                      className="flex items-center text-sm text-slate-500 font-medium"
                    >
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="text-[#4d2a8a] mr-2"
                      />{" "}
                      {feat}
                    </li>
                  ))}
                </ul>
              </div>
            </RevealOnScroll>

            {/* 1. Global Payment Gateway */}
            <RevealOnScroll className="delay-0">
              <div className="group bg-white rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-slate-200/60 hover:border-blue-100 h-full flex flex-col">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center text-2xl group-hover:bg-blue-600 group-hover:text-white transition-colors">
                    <FontAwesomeIcon icon={faGlobe} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">
                    {t("landing.featured.gateway.title")}
                  </h3>
                </div>
                <p className="text-slate-700 mb-6 flex-grow leading-relaxed">
                  {t("landing.featured.gateway.desc")}
                </p>
                <ul className="space-y-2 mb-6">
                  {[
                    t("landing.featured.gateway.feat1"),
                    t("landing.featured.gateway.feat2"),
                    t("landing.featured.gateway.feat3"),
                  ].map((feat, i) => (
                    <li
                      key={i}
                      className="flex items-center text-sm text-slate-500 font-medium"
                    >
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="text-green-500 mr-2"
                      />{" "}
                      {feat}
                    </li>
                  ))}
                </ul>
              </div>
            </RevealOnScroll>

            {/* 2. Invoice & Billing Suite */}
            <RevealOnScroll className="delay-100">
              <div className="group bg-white rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-slate-200/60 hover:border-[#5556fd]/30 h-full flex flex-col">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-[#5556fd]/10 text-[#5556fd] rounded-xl flex items-center justify-center text-2xl group-hover:bg-[#5556fd] group-hover:text-white transition-colors">
                    <FontAwesomeIcon icon={faFileInvoiceDollar} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">
                    {t("landing.featured.invoice.title")}
                  </h3>
                </div>
                <p className="text-slate-600 mb-6 flex-grow leading-relaxed">
                  {t("landing.featured.invoice.desc")}
                </p>
                <ul className="space-y-2 mb-6">
                  {[
                    t("landing.featured.invoice.feat1"),
                    t("landing.featured.invoice.feat2"),
                    t("landing.featured.invoice.feat3"),
                  ].map((feat, i) => (
                    <li
                      key={i}
                      className="flex items-center text-sm text-slate-500 font-medium"
                    >
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="text-[#5556fd] mr-2"
                      />{" "}
                      {feat}
                    </li>
                  ))}
                </ul>
              </div>
            </RevealOnScroll>

            {/* 3. Logistics & Mail System */}

            {/* 4. Global Account Solutions */}
            <RevealOnScroll className="delay-300">
              <div className="group bg-white rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-slate-200/60 hover:border-blue-100 h-full flex flex-col">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center text-2xl group-hover:bg-purple-600 group-hover:text-white transition-colors">
                    <FontAwesomeIcon icon={faIdCard} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">
                    {t("landing.featured.account.title")}
                  </h3>
                </div>
                <p className="text-slate-600 mb-6 flex-grow leading-relaxed">
                  {t("landing.featured.account.desc")}
                </p>
                <ul className="space-y-2 mb-6">
                  {[
                    t("landing.featured.account.feat1"),
                    t("landing.featured.account.feat2"),
                    t("landing.featured.account.feat3"),
                  ].map((feat, i) => (
                    <li
                      key={i}
                      className="flex items-center text-sm text-slate-500 font-medium"
                    >
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="text-purple-500 mr-2"
                      />{" "}
                      {feat}
                    </li>
                  ))}
                </ul>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* Management Modules Section */}
      <section id="products" className="py-20 bg-white text-slate-900 relative">
        {/* Top Wave Transition */}
        <div className="absolute -top-[0.1rem] left-0 w-full overflow-hidden leading-[0] z-30">
          <svg
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            className="relative block w-[calc(100%+1.3px)] h-[50px]"
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
              className="fill-slate-900 main-svg-fill-color"
            ></path>
          </svg>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <RevealOnScroll className="text-center mb-20">
            <span className="text-blue-600 font-bold uppercase tracking-wider text-sm">
              {t("email.ecosystem.subtitle") || "管理系统"}
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mt-3 mb-6 text-slate-900">
              {t("email.ecosystem.title1") || "完善的管理模块"} <br />
              <span className="text-blue-600">
                {t("email.ecosystem.title2") || "一站式管理"}
              </span>
            </h2>
            <p className="text-xl text-slate-500 max-w-2xl mx-auto">
              {t("email.ecosystem.description") ||
                "六大核心管理模块，满足企业级邮件管理需求"}
            </p>
          </RevealOnScroll>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {managementModules.map((module, idx) => (
              <RevealOnScroll
                key={module.id}
                className={`delay-[${idx * 100}ms] h-full`}
              >
                <div
                  onClick={() => navigate(module.link)}
                  className="bg-slate-50 rounded-2xl p-8 shadow-sm hover:shadow-2xl hover:bg-white transition-all duration-300 border border-slate-100 hover:border-blue-200 group h-full hover:-translate-y-2 cursor-pointer"
                >
                  <div
                    className={`w-14 h-14 rounded-xl ${module.color} text-white flex items-center justify-center text-2xl mb-6 shadow-md transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}
                  >
                    <FontAwesomeIcon icon={module.icon} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors">
                    {module.title}
                  </h3>
                  <p className="text-slate-500 mb-6 min-h-[3rem] group-hover:text-slate-600 transition-colors">
                    {module.description}
                  </p>
                  <div className="space-y-3 mb-8">
                    {module.features.map((feature, i) => (
                      <div
                        key={i}
                        className="flex items-center text-sm text-slate-500 font-medium"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-2 group-hover:bg-blue-400 transition-colors"></div>
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Analytics Section */}
      <section
        id="analytics"
        className="py-20 bg-white relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-1/3 h-full bg-blue-50/50 blur-3xl rounded-l-full"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row items-center gap-16">
            <div className="md:w-1/2">
              <RevealOnScroll>
                <div className="inline-block px-4 py-1 bg-blue-100 text-blue-600 rounded-full text-sm font-bold mb-6">
                  {t("email.analytics.badge") || "数据分析"}
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                  {t("email.analytics.title1") || "邮件数据统计"} <br />
                  <span className="text-blue-600">
                    {t("email.analytics.title2") || "可视化分析"}
                  </span>
                </h2>
                <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                  {t("email.analytics.description") ||
                    "实时监控邮件发送数据，了解投递状态与系统运行情况"}
                </p>
                <ul className="space-y-4 mb-10">
                  {[
                    t("email.analytics.feat1") || "邮件发送成功率统计",
                    t("email.analytics.feat2") || "发送趋势图表展示",
                    t("email.analytics.feat3") || "退信与失败分析",
                    t("email.analytics.feat4") || "实时发送状态监控",
                  ].map((item, i) => (
                    <li
                      key={i}
                      className="flex items-center text-slate-700 font-medium"
                    >
                      <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3 text-xs">
                        <FontAwesomeIcon icon={faCheckCircle} />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </RevealOnScroll>
            </div>

            <div className="md:w-1/2 flex justify-center w-full">
              <ConversationalCommerce />
            </div>
          </div>
        </div>
      </section>

      {/* Tech Features Grid */}
      <section id="features" className="py-20  relative bg-slate-900">
        {/* Top Wave Transition */}
        <div className="absolute -top-[0.05rem] left-0 w-full overflow-hidden leading-[0]">
          <svg
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            class="relative block w-[calc(100%+1.3px)] h-[50px]"
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
              class="fill-white"
            ></path>
          </svg>
        </div>
        <div className="container mx-auto px-6 relative z-10 pt-10">
          <RevealOnScroll className="text-center mb-20">
            <span className="text-blue-400 font-bold uppercase tracking-wider text-sm">
              {t("email.features.subtitle") || "核心功能"}
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mt-3 mb-6 text-slate-900 text-white">
              {t("email.features.title") || "专业的邮件管理"}
            </h2>
            <p className="text-xl text-slate-500 max-w-2xl mx-auto text-white">
              {t("email.features.description") ||
                "一站式邮件管理解决方案，满足企业级邮件发送需求"}
            </p>
          </RevealOnScroll>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {techFeatures.map((feature, idx) => (
              <RevealOnScroll key={idx} className="h-full">
                <div className="group p-8 rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-300 border border-slate-100 group h-full">
                  <div className="w-14 h-14 bg-blue-500/10 text-blue-600 rounded-xl flex items-center justify-center text-2xl mb-6 group-hover:bg-blue-500 group-hover:text-white transition-colors">
                    <FontAwesomeIcon icon={feature.icon} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-slate-500 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section id="trust" className="py-24 bg-white relative z-20 ">
        <div className="container mx-auto px-6 flex flex-col md:flex-row items-center">
          <div className="md:w-1/2 mb-12 md:mb-0 pr-0 md:pr-12">
            <RevealOnScroll>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">
                {t("landing.trust.title")}
              </h2>
              <div className="space-y-6">
                {[
                  t("landing.trust.item1"),
                  t("landing.trust.item2"),
                  t("landing.trust.item3"),
                  t("landing.trust.item4"),
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start">
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="text-green-500 mt-1 mr-4"
                    />
                    <span className="text-lg text-slate-600">{item}</span>
                  </div>
                ))}
              </div>
              {/* <div className="mt-10">
								<Link to="#" className="text-blue-600 font-semibold hover:text-blue-700 transition-colors flex items-center">
									View our certifications <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
								</Link>
							</div> */}
            </RevealOnScroll>
          </div>
          <div className="md:w-1/2 relative">
            <RevealOnScroll className="delay-200">
              <div className="absolute inset-0 bg-blue-100 rounded-full filter blur-[100px] opacity-50"></div>
              <div className="w-[340px] sm:w-auto relative bg-white/50 backdrop-blur-xl rounded-2xl p-8 border border-slate-200 shadow-2xl">
                <div className="grid grid-cols-2 gap-4">
                  {["102.png", "101.png", "103.png", ""].map((logo, i) => (
                    <div
                      key={i}
                      className="h-24 bg-white rounded-lg flex items-center justify-center border border-slate-100 p-4 hover:shadow-md transition-shadow duration-300"
                    >
                      <img
                        src={`/logos/${logo}`}
                        alt={`Trust Logo ${i + 1}`}
                        className="max-h-full max-w-full object-contain  hover:grayscale-0 transition-all duration-300"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-8 pt-8 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-3xl font-bold text-slate-900">
                        2M+
                      </div>
                      <div className="text-sm text-slate-500">
                        {t("landing.trust.transactions")}
                      </div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-slate-900">
                        $50B+
                      </div>
                      <div className="text-sm text-slate-500">
                        {t("landing.trust.volume")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24   relative overflow-hidden  bg-slate-900">
        {/* <div className="absolute -top-[0.1rem] left-0 w-full overflow-hidden leading-[0] z-30">
					<svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none" shapeRendering="geometricPrecision" className="relative block w-[calc(100%+1.3px)] h-[50px]">
						<path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-white"></path>
					</svg>
				</div> */}
        <div className="absolute inset-0 from-blue-900/20 to-purple-900/20"></div>
        <div className="container mx-auto px-6 relative z-10 text-center">
          <RevealOnScroll>
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-8">
              {t("landing.cta.title")}
            </h2>
            <p className="text-xl text-blue-100 mb-12 max-w-2xl mx-auto">
              {t("landing.cta.description")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {isOfficialSite ? (
                <a
                  href={loginUrl}
                  className="w-full sm:w-auto px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-full font-bold text-lg transition-all transform hover:scale-105 shadow-lg shadow-blue-900/50 "
                >
                  {t("landing.cta.getStart")}
                </a>
              ) : (
                <Link
                  to="/login"
                  className="w-full sm:w-auto px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-full font-bold text-lg transition-all transform hover:scale-105 shadow-lg shadow-blue-900/50 "
                >
                  {t("landing.cta.getStart")}
                </Link>
              )}
              {/* <a href={telegramLink} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto px-8 py-4 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 rounded-full font-bold text-lg transition-all transform hover:scale-105 shadow-lg flex items-center justify-center gap-2 group">
								<FontAwesomeIcon icon={faTelegram} className="text-xl group-hover:scale-110 transition-transform" />
								<span>
									{t("landing.cta.contactSales")}: {telegram}
								</span>
							</a> */}
            </div>
          </RevealOnScroll>
        </div>
      </section>
    </div>
  );
}

export default LandingPage;
